import { useEffect } from "react";
import { useState } from "react";
import styled, { css, keyframes } from "styled-components";
import theme from "../../../UI/theme";
import playFromTypes from "../../musicPlayer/playFromTypes";
import Button from "../../UiComponents/Button";
import Modal from "../../UiComponents/Modal"
import Selector from "../../UiComponents/Selector";
import { ModalContainer, ModalHeader, ModalText } from "../ModalStyles";
import { hasVenuePermission, permissionTypes } from "../../permission/permissions";
import AiFlairText from "../../UiComponents/AiFlairText";
import Dropdown from "../../UiComponents/Dropdown";

const borderShift = keyframes`
  0%   { background-position: 0% 50%; }
  50%  { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const PlayFromItem = styled.div`
    display: grid;
    grid-template-columns: 1fr auto auto;
    grid-gap: 10px;
    background-color: ${theme.colors.darkSpace90};
    border: 1px solid transparent;
    padding: 10px;
    box-sizing: border-box;
    border-radius: 2px;
    align-items: center;
    height: fit-content;
    row-gap: 10px;

    ${props => props.selected && css`
        // background-color: ${theme.colors.greenEnergy};
        border-color: ${theme.colors.greenEnergy};
    `}

    ${props => props.isHighestMoodMatch && css`
        
        border: 2px solid transparent;

    background:
      linear-gradient(${theme.colors.darkSpace90}, ${theme.colors.darkSpace90}) padding-box,
      linear-gradient(90deg, #A8DC00, #4bc0c0, #A8DC00) border-box;

    background-size: auto, 300% 300%;
    background-position: 0 0, 0% 50%;
    animation: ${borderShift} 10s linear infinite;

    `}

    .mood-match-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        grid-column: 1 / -1;
    }
`

const PlayFromList = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    grid-gap: 10px;
    width: 100%;
    max-height: 475px;
    overflow: auto;
`

const PlaylistTitle = styled.label`
    
    ${props => props.selected && css`
        color: ${theme.colors.greenEnergy};
    `}
`;

/**
 * 
 TODO: if musicplayer.hasStoredPlaylists is false, disable the settings for virtual dj. add some flair to please fredrik
 */

const orderPlaylistsByItems = {
    MOOD_MATCH: {
        value: 'MOOD_MATCH',
        label: 'Mood Match',
    },
    PLAYLIST_NAME: {
        value: 'PLAYLIST_NAME',
        label: 'Playlist Name',
    },
    PLAYLIST_MOOD: {
        value: 'PLAYLIST_MOOD',
        label: 'Playlist Mood',
    },
    PLAYING: {
        value: 'PLAYING',
        label: 'Currently Playing',
    }
}

const PlayFromModal = props => {
    const availableTabs = resolveAvailableTabs(props.musicPlayer);
    const [selectedTab, setSelectedTab] = useState();
    const [playingFrom, setPlayingFrom] = useState(props.musicPlayer?.music?.playFrom?.id);
    const [useInVirtualDjValues, setUseInVirtualDjValues] = useState({});
    const [orderPlaylistsBy, setOrderPlaylistsBy] = useState(orderPlaylistsByItems.MOOD_MATCH);

    


    useEffect(() => {
        setPlayingFrom(props.musicPlayer?.music?.playFrom?.id);
    }, [props.musicPlayer?.music?.playFrom?.id])

    useEffect(() => {
        if (!selectedTab) {
            setSelectedTab(availableTabs[0] ?? null);
        }
    }, [availableTabs])

    useEffect(() => {
        if (props.musicPlayer?.music?.availablePlaylists?.length) {
            const initialUseInVirtualDjValues = {};
            props.musicPlayer.music.availablePlaylists.forEach(playlist => {
                const remotePlaylistId = getRemotePlaylistKey(playlist);
                initialUseInVirtualDjValues[remotePlaylistId] = playlist?.useInVirtualDj || false;
            });
            setUseInVirtualDjValues(initialUseInVirtualDjValues);
        }
    }, [props.musicPlayer?.music?.availablePlaylists]);

    const getRemotePlaylistKey = (p) => p?.remotePlaylistId || p?.id;

    const changePlayList = async (playlist) => {
        if (!hasVenuePermission(props.userContext, permissionTypes.panelViewWrite)) {
            return;
        }

        const remotePlaylistId = getRemotePlaylistKey(playlist);
        
        let result = await props.musicPlayer?.playPlaylist(remotePlaylistId);
        if (result === true) {
            setPlayingFrom(remotePlaylistId);
        }
    }

    const queuePlaylist = (playlist) => {
        if (!hasVenuePermission(props.userContext, permissionTypes.panelViewWrite)) {
            return;
        }
        const remotePlaylistId = getRemotePlaylistKey(playlist);
        props.musicPlayer?.queuePlaylist(remotePlaylistId);
    }

    const orderPlaylists = (a, b)  => {

        switch (orderPlaylistsBy.value) {
            case orderPlaylistsByItems.MOOD_MATCH.value:
                return (b.moodMatch ?? 0) - (a.moodMatch ?? 0);
            case orderPlaylistsByItems.PLAYLIST_NAME.value:
                return a.name.localeCompare(b.name);
            case orderPlaylistsByItems.PLAYLIST_MOOD.value:
                return (b.moodScore || 0) - (a.moodScore || 0);
            case orderPlaylistsByItems.PLAYING.value:
                return 0;
            default:
                return 0;
        }

    }

    return <Modal {...props}>
        <ModalContainer>
            { Object.keys(playFromTypes).includes(selectedTab?.key) ? <>
                {selectedTab?.key === playFromTypes.PLAYLIST?.key && <ModalHeader>Playlists</ModalHeader>}
                {selectedTab?.key === playFromTypes.SCHEDULE?.key && <ModalHeader>Schedules</ModalHeader>}
            </> : <>
                <ModalHeader>Manage your Music</ModalHeader>
            </>}


            {availableTabs?.length > 1 ?
                <Selector items={availableTabs} selectedItem={selectedTab} onClick={(tab) => setSelectedTab(tab)} />
                : <></>}

            <PlayFromList>
                {selectedTab?.key === playFromTypes.PLAYLIST.key ? <>
                    <ModalText style={{margin: '6px 0px 0px 0px'}}>
                        Currently Playing: &nbsp;
                            <span style={{color: theme.colors.greenEnergy}}>
                                {[...(props.musicPlayer?.music?.availablePlaylists || [])].find(p => getRemotePlaylistKey(p) === playingFrom)?.name || "None"}
                            </span>
                    </ModalText>
                    <ModalText>Order Playlists By:</ModalText>
                    <Dropdown
                        small
                        items={Object.values(orderPlaylistsByItems)}
                        selectedItem={orderPlaylistsBy}
                        onClick={(item) => setOrderPlaylistsBy(item)} 
                    />

                    {[...(props.musicPlayer?.music?.availablePlaylists || [])]
                        .sort((a, b) => {

                            const aUse = useInVirtualDjValues[a.remotePlaylistId] ?? a.useInVirtualDj ?? false;
                            const bUse = useInVirtualDjValues[b.remotePlaylistId] ?? b.useInVirtualDj ?? false;

                            // First, prioritize playlists used in Virtual DJ if Virtual DJ mode is ON and ordering by MOOD MATCH
                            if (props.musicPlayer?.virtualDjMode !== 'OFF' && orderPlaylistsBy.value === orderPlaylistsByItems.MOOD_MATCH.value) {
                                if (aUse && !bUse) return -1;
                                if (!aUse && bUse) return 1;
                            }

                            // Next, prioritize currently playing playlist if ordering by PLAYING
                            if (orderPlaylistsBy.value === orderPlaylistsByItems.PLAYING.value) {
                                if (a.remotePlaylistId === playingFrom) return -1;
                                if (b.remotePlaylistId === playingFrom) return 1;
                            }
                            
                            // Finally, apply the selected ordering
                            return orderPlaylists(a, b);
                        })
                        .map((playlist, index) => {

                        const remotePlaylistId = getRemotePlaylistKey(playlist);

                        const isHighestMoodMatch = playlist.remotePlaylistId === props.bestMatchPlaylist?.remotePlaylistId;
                        const isSelected = (playingFrom === playlist?.remotePlaylistId) || (playingFrom === playlist?.id);

                        return <PlayFromItem key={remotePlaylistId} selected={isSelected} isHighestMoodMatch={isHighestMoodMatch}>
                            {isHighestMoodMatch ?
                                <AiFlairText
                                    text={`${playlist?.name} - (Best Match)`}
                                    fontSize={"16px"}
                                    mobileFontSize={"14px"}
                                    simpleText={true}
                                />
                            :
                                <PlaylistTitle selected={isSelected} >{playlist?.name} {isSelected ? "(Playing)" : ""}</PlaylistTitle>
                            }
                            
                            <Button small tertiary onClick={() => queuePlaylist(playlist)}>Queue</Button>
                            <Button small tertiary onClick={() => changePlayList(playlist)}>Play now</Button>
                            <div className="mood-match-row">
                                {isHighestMoodMatch ?
                                    <AiFlairText
                                        text={`Mood Match: ${playlist?.moodMatch}%`}
                                        fontSize={"16px"}
                                        mobileFontSize={"14px"}
                                        simpleText={true}
                                    />
                                :
                                    <label>Mood Match: {playlist?.moodScore? `${playlist.moodMatch}%` : "N/A"}</label>    
                                }
                            </div>
                        </PlayFromItem>
                    })}
                </> : <></>}

                {selectedTab?.key === playFromTypes.SCHEDULE.key ? <>
                    {props.musicPlayer?.music?.availableSchedules?.map((schedule, index) => {
                        return <PlayFromItem key={index} selected={playingFrom === schedule?.id}>
                            <label>{schedule?.name}</label>
                            <Button small tertiary onClick={() => queuePlaylist(schedule)}>Queue</Button>
                            <Button small tertiary onClick={() => changePlayList(schedule)}>Play now</Button>
                        </PlayFromItem>
                    })}
                </> : <></>}
            </PlayFromList>

        </ModalContainer>
    </Modal>
}

export default PlayFromModal;

const resolveAvailableTabs = (musicPlayer) => {
    let availableTabs = [];
    if (musicPlayer?.music?.availablePlaylists?.length > 0) {
        availableTabs.push({
            display: "Playlists",
            key: playFromTypes.PLAYLIST.key,
        });
    }

    if (musicPlayer?.music?.availableSchedules?.length > 0) {
        availableTabs.push({
            display: "Schedules",
            key: playFromTypes.SCHEDULE.key,
        })
    }

    return availableTabs;
}